import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Form,
    SelectPicker,
    InputNumber,
    Input,
    Message,
    useToaster,
    ButtonGroup,
    Loader,
    Checkbox,
  } from 'rsuite';
  import authFetch from '../../helpers/authFetch';
  import { CONDITION_OPTIONS, FINISH_OPTIONS } from './CardEntryFormOptions';

  const PRINTING_PREVIEW_HEIGHT = 70;
  const PRINTING_PREVIEW_BORDER_RADIUS = 4;
  const PRINTING_PREVIEW_MARGIN_TOP = 8;
  const MOVE_ZONE_MARGIN_TOP = 8;
  const MOVE_ZONE_BORDER_RADIUS = 4;
  const MOVE_ZONE_PADDING = 12;
  const COLORS = {
    moveZoneBorder: '#3c3c3c',
    moveZoneBackground: '#1a1a1a',
  };

  const EditEntryModal = ({ open, onClose, entry, collectionId, deckType, collectionType, onUpdated }) => {
    const [formData, setFormData] = useState({
      scryfall_id: null,
      quantity: 1,
      condition: 'NM',
      finish: 'nonfoil',
      purchase_price: '',
      notes: '',
      is_commander: false,
      is_signature_spell: false,
      is_proxy: false,
      is_not_owned: false,
    });
   const [error, setError] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [printings, setPrintings] = useState([]);
   const [loadingPrintings, setLoadingPrintings] = useState(false);
   const [originalScryfallId, setOriginalScryfallId] = useState(null);
   const [moveQty, setMoveQty] = useState(1);
   const [movingZone, setMovingZone] = useState(false);
   const toaster = useToaster();

  useEffect(() => {
    if (open && entry) {
      const finish = entry.finish || 'nonfoil';
      setFormData({
        scryfall_id: entry.scryfall_id,
        quantity: entry.quantity || 1,
        condition: entry.condition || 'NM',
        finish: finish,
        purchase_price: entry.purchase_price != null ? entry.purchase_price : '',
        notes: entry.notes || '',
        is_commander: entry.is_commander || false,
        is_signature_spell: entry.is_signature_spell || false,
        is_proxy: entry.is_proxy || false,
        is_not_owned: entry.is_not_owned || false,
      });
      setOriginalScryfallId(entry.scryfall_id);
      setMoveQty(1);
      setError('');

      // Fetch printings for this card
      const fetchPrintings = async () => {
        setLoadingPrintings(true);
        try {
          const response = await authFetch(`/cards/${entry.scryfall_id}/printings`);
          if (response.ok) {
            const data = await response.json();
            setPrintings(data);
          }
        } catch (err) {
          // silent fail - graceful degradation
        } finally {
          setLoadingPrintings(false);
        }
      };

      if (entry.scryfall_id) {
        fetchPrintings();
      }
    }
  }, [open, entry]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const adjustQuantity = (delta) => {
    setFormData((prev) => ({
      ...prev,
      quantity: Math.max(1, (prev.quantity || 1) + delta),
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const body = {
        quantity: formData.quantity,
        condition: formData.condition,
        finish: formData.finish,
      };

      // Only send scryfall_id if it changed
      if (formData.scryfall_id !== originalScryfallId) {
        body.scryfall_id = formData.scryfall_id;
      }

      if (formData.purchase_price !== '') {
        body.purchase_price = parseFloat(formData.purchase_price);
      } else {
        body.purchase_price = null;
      }

      body.notes = formData.notes.trim() || null;
      body.is_proxy = formData.is_proxy;
      body.is_not_owned = formData.is_not_owned;

      // Include commander/signature spell flags if applicable
      if (deckType === 'COMMANDER' || deckType === 'OATHBREAKER') {
        body.is_commander = formData.is_commander;
      }
      if (deckType === 'OATHBREAKER') {
        body.is_signature_spell = formData.is_signature_spell;
      }

      const response = await authFetch(
        `/collections/${collectionId}/entries/${entry.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to update entry');
        return;
      }

      toaster.push(
        <Message type="success" showIcon closable>
          <strong>{entry.name}</strong> updated
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );

      handleClose();
      if (onUpdated) onUpdated({ ...formData });
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  const targetZoneLabel = entry?.is_sideboard ? 'mainboard' : 'sideboard';

  const handleMoveZone = async () => {
    setError('');
    setMovingZone(true);
    try {
      const response = await authFetch(
        `/collections/${collectionId}/entries/${entry.id}/move-zone`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ move_quantity: moveQty }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to move entry');
        return;
      }

      toaster.push(
        <Message type="success" showIcon closable>
          Moved {moveQty} {moveQty === 1 ? 'copy' : 'copies'} of <strong>{entry.name}</strong> to {targetZoneLabel}
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );

      handleClose();
      if (onUpdated) onUpdated();
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setMovingZone(false);
    }
  };

  if (!entry) return null;

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>Edit Entry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: 16 }}>
          Editing <strong>{entry.name}</strong>
        </p>

        {error && (
          <Message type="error" showIcon style={{ marginBottom: 16 }}>
            {error}
          </Message>
        )}

        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Quantity</Form.ControlLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ButtonGroup>
                <Button onClick={() => adjustQuantity(-1)} disabled={formData.quantity <= 1}>
                  −
                </Button>
                <Button onClick={() => adjustQuantity(1)}>+</Button>
              </ButtonGroup>
              <InputNumber
                value={formData.quantity}
                onChange={(value) => setField('quantity', parseInt(value, 10) || 1)}
                min={1}
                max={999}
                style={{ flex: 1 }}
              />
            </div>
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Condition</Form.ControlLabel>
            <SelectPicker
              data={CONDITION_OPTIONS}
              value={formData.condition}
              onChange={(value) => setField('condition', value)}
              searchable={false}
              block
              placeholder="Select condition"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Finish</Form.ControlLabel>
            <SelectPicker
              key={`finish-${entry?.id}`}
              data={FINISH_OPTIONS}
              value={formData.finish}
              onChange={(value) => setField('finish', value)}
              searchable={false}
              block
              placeholder="Select finish"
            />
          </Form.Group>

          {!loadingPrintings && printings.length > 0 && (
            <Form.Group>
              <Form.ControlLabel>Printing</Form.ControlLabel>
              {loadingPrintings ? (
                <Loader content="Loading printings..." />
              ) : (
                <>
                  <SelectPicker
                    data={printings.map((p) => ({
                      label: `${p.set_name} (${p.collector_number})`,
                      value: p.id,
                    }))}
                    value={formData.scryfall_id}
                    onChange={(value) => setField('scryfall_id', value)}
                    searchable={false}
                    block
                    placeholder="Select printing"
                  />
                  {formData.scryfall_id && (
                    <div style={{ marginTop: PRINTING_PREVIEW_MARGIN_TOP }}>
                      {(() => {
                        const selected = printings.find((p) => p.id === formData.scryfall_id);
                        if (selected && selected.image_uris) {
                          return (
                            <img
                              src={selected.image_uris.normal}
                              alt={selected.name}
                              style={{ height: PRINTING_PREVIEW_HEIGHT, borderRadius: PRINTING_PREVIEW_BORDER_RADIUS }}
                            />
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </>
              )}
            </Form.Group>
          )}

          <Form.Group>
            <Form.ControlLabel>Purchase Price (optional)</Form.ControlLabel>
            <InputNumber
              value={formData.purchase_price}
              onChange={(value) => setField('purchase_price', value)}
              min={0}
              step={0.01}
              prefix="$"
              style={{ width: '100%' }}
              disabled={formData.is_not_owned}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Notes (optional)</Form.ControlLabel>
            <Input
              as="textarea"
              rows={2}
              value={formData.notes}
              onChange={(value) => setField('notes', value)}
              placeholder="Optional notes"
            />
          </Form.Group>

          {collectionType === 'DECK' && (
            <Form.Group>
              <Checkbox
                checked={formData.is_proxy}
                onChange={(_, checked) => {
                  setField('is_proxy', checked);
                  if (checked) setField('is_not_owned', false);
                }}
              >
                Proxy copy
              </Checkbox>
            </Form.Group>
          )}

          {collectionType === 'DECK' && (
            <Form.Group>
              <Checkbox
                checked={formData.is_not_owned}
                onChange={(_, checked) => {
                  setField('is_not_owned', checked);
                  if (checked) {
                    setField('purchase_price', '');
                    setField('is_proxy', false);
                  }
                }}
              >
                I don't own this card
              </Checkbox>
            </Form.Group>
          )}

          {collectionType === 'DECK' && (
            <Form.Group>
              <div
                style={{
                  marginTop: MOVE_ZONE_MARGIN_TOP,
                  border: `1px solid ${COLORS.moveZoneBorder}`,
                  borderRadius: MOVE_ZONE_BORDER_RADIUS,
                  padding: MOVE_ZONE_PADDING,
                }}
              >
                <Form.ControlLabel>
                  Move copies to {targetZoneLabel}
                </Form.ControlLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <InputNumber
                    value={moveQty}
                    onChange={(value) => setMoveQty(Math.max(1, Math.min(formData.quantity, parseInt(value, 10) || 1)))}
                    min={1}
                    max={formData.quantity}
                    style={{ width: 80 }}
                  />
                  <span style={{ flex: 1 }}>
                    {moveQty === formData.quantity
                      ? `Move all ${moveQty} ${moveQty === 1 ? 'copy' : 'copies'} to ${targetZoneLabel}`
                      : `Move ${moveQty} of ${formData.quantity} ${formData.quantity === 1 ? 'copy' : 'copies'} to ${targetZoneLabel}`}
                  </span>
                  <Button
                    appearance="ghost"
                    size="sm"
                    onClick={handleMoveZone}
                    loading={movingZone}
                    disabled={submitting}
                  >
                    Move
                  </Button>
                </div>
              </div>
            </Form.Group>
          )}

          {(deckType === 'COMMANDER' || deckType === 'OATHBREAKER') && (
            <Form.Group>
              <Checkbox
                checked={formData.is_commander}
                onChange={(_, checked) => {
                  setField('is_commander', checked);
                  if (checked && deckType === 'OATHBREAKER') setField('is_signature_spell', false);
                }}
              >
                {deckType === 'OATHBREAKER' ? 'Oathbreaker' : 'Commander'}
              </Checkbox>
            </Form.Group>
          )}

          {deckType === 'OATHBREAKER' && (
            <Form.Group>
              <Checkbox
                checked={formData.is_signature_spell}
                onChange={(_, checked) => {
                  setField('is_signature_spell', checked);
                  if (checked) setField('is_commander', false);
                }}
              >
                Signature Spell
              </Checkbox>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle">
          Cancel
        </Button>
        <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditEntryModal;
